import React, { useState, useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import client from '../../utils/contentfulClient';
import ScrollFadeWrapper from '../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../HomePage/LinkWithIcon/LinkWithIconComponent';
import Image from '../Image/Image';

const formatBlogDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const normalizeSlug = (slug) => slug.replace(/\/$/, '');

const getSlugValue = (entry) => (Array.isArray(entry) ? entry[0] : entry);
const getTitleOverride = (entry) =>
  Array.isArray(entry) ? entry[1] : undefined;

const mapPostToCard = (post, titleOverride) => {
  const { slug, category, heading, blogDate, headingImage } = post.fields;
  const cleanSlug = normalizeSlug(slug);
  const link = cleanSlug.includes('/')
    ? `/blog/${slug}`
    : `/blog/${category}/${slug}`;
  return {
    image: headingImage?.fields?.file?.url
      ? `https:${headingImage.fields.file.url}`
      : '',
    date: formatBlogDate(blogDate),
    title: titleOverride || heading,
    link,
  };
};

const VerticalSeoSubjectGuideComponent = ({ data }) => {
  const {
    title,
    description,
    footer,
    footerLink,
    footerLinl,
    slugs = [],
  } = data;
  const [cards, setCards] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const resolvedFooterLink = footerLink || footerLinl;

  useEffect(() => {
    if (!slugs.length) {
      setCards([]);
      setHasLoaded(true);
      return;
    }

    const fetchCards = async () => {
      try {
        const slugVariants = Array.from(
          new Set(
            slugs.flatMap((entry) => {
              const s = getSlugValue(entry);
              return [normalizeSlug(s), `${normalizeSlug(s)}/`];
            })
          )
        );

        const response = await client.getEntries({
          content_type: 'blogPost',
          'fields.slug[in]': slugVariants.join(','),
        });

        const bySlug = new Map(
          response.items.map((post) => [normalizeSlug(post.fields.slug), post])
        );

        const ordered = slugs
          .map((entry) => ({
            post: bySlug.get(normalizeSlug(getSlugValue(entry))),
            titleOverride: getTitleOverride(entry),
          }))
          .filter(({ post }) => Boolean(post))
          .map(({ post, titleOverride }) => mapPostToCard(post, titleOverride));

        setCards(ordered);
      } catch (err) {
        // ignore
      } finally {
        setHasLoaded(true);
      }
    };

    fetchCards();
  }, [slugs]);

  if (hasLoaded && cards.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '1216px',
          borderRadius: '48px',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        {/* Header */}
        <ScrollFadeWrapper variant="text">
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        {/* Description */}
        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '40px',
              maxWidth: '980px',
              mx: 'auto',
            }}
          >
            {description}
          </Typography>
        </ScrollFadeWrapper>

        {/* Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
            mb: '40px',
          }}
        >
          {cards.map((card, index) => (
            <ScrollFadeWrapper key={index} delay={(index % 3) * 0.15}>
              <Box
                sx={{
                  height: '100%',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '34px',
                  boxShadow: `0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Image */}
                <Image
                  width={390}
                  height={219}
                  src={card.image}
                  alt={card.title}
                  sx={{
                    width: '100%',
                    height: '219px',
                    objectFit: 'cover',
                    backgroundColor: '#A2ACB1',
                  }}
                />

                {/* Content */}
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    pt: '40px',
                    pb: '40px',
                  }}
                >
                  <Box sx={{ px: '40px' }}>
                    {/* Date */}
                    <Typography
                      component={'p'}
                      sx={{
                        fontFamily: 'Overline, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: '1.25rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        textAlign: 'right',
                        mb: '16px',
                      }}
                    >
                      {card.date}
                    </Typography>

                    {/* Title */}
                    <Typography
                      component={'h6'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                        textAlign: 'left',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>

                  {/* Link */}
                  <Box sx={{ textAlign: 'right', px: '40px', mt: '16px' }}>
                    <Link
                      href={card.link}
                      underline="none"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        verticalAlign: 'middle',
                        '&:hover': {
                          color: '#9EA900',
                        },
                      }}
                    >
                      Read More
                    </Link>
                  </Box>
                </Box>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>

        {/* Footer */}
        {footer &&
          (resolvedFooterLink ? (
            <LinkWithIconComponent
              href={resolvedFooterLink}
              iconSize="1rem"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#162F3B',
                maxWidth: '596px',
                mx: 'auto',
                '&:hover': {
                  color: '#9EA900',
                },
              }}
            >
              {footer}
            </LinkWithIconComponent>
          ) : (
            <Typography
              component={'p'}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#162F3B',
                maxWidth: '596px',
                mx: 'auto',
              }}
            >
              {footer}
            </Typography>
          ))}
      </Box>
    </Box>
  );
};

export default VerticalSeoSubjectGuideComponent;
